import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';

import { Ati } from '@atlassian/ari';
import { getAtlassianStudioNLCreateUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ItemTitleWithLozenge } from '../ItemTitle.tsx';
import { useAgentNewLozenge } from '../../useAgentNewLozenge.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

const CREATE_AGENT_ACTION_SUBJECT_ID = 'createAgentPopoverItem';
const CREATE_AGENT_ATI = 'ati:cloud:rovo:agent';
const CONNIE_CREATE_SOURCE = 'connie-create';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.agent.title.v2',
		defaultMessage: 'Agent',
		description: 'Title indicating that this option will redirect to the Studio Create Agent page',
	},
	newLozenge: {
		id: 'create-blank-fabric-page.universal-create.agent.new-lozenge',
		defaultMessage: 'New',
		description: 'Text for the new lozenge indicating this is a new feature',
	},
	newLozengeUpdated: {
		id: 'create-blank-fabric-page.universal-create.agent.new-lozenge.updated',
		defaultMessage: 'New',
		description: 'Sentence case text for the new lozenge indicating this is a new feature',
	},
});

const StudioRedirectCreateAgentMenuItem = ({
	source,
}: Partial<Pick<UseMenuItemProps, 'source'>>) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const shouldShowNewLozenge = useAgentNewLozenge({
		isEnabled: fg('cc_fd_agent_creation_connie_lozenge'),
	});

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'createAgentPopoverItem',
				attributes: {
					source,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	return shouldShowNewLozenge ? (
		<ItemTitleWithLozenge lozenge={formatMessage(messages.newLozengeUpdated)}>
			{formatMessage(messages.title)}
		</ItemTitleWithLozenge>
	) : (
		<>{formatMessage(messages.title)}</>
	);
};

export function useStudioRedirectCreateAgentItem({
	onClick,
	source,
	isLoading,
}: UseMenuItemProps & { isLoading?: boolean }): UseMenuItemReturnProps {
	const { cloudId } = useSessionData();
	const [csid, { trackCreateSessionStart }] = useRovoAgentCreateAnalytics({
		touchPoint: CONNIE_CREATE_SOURCE,
	});

	const createAgentUrl = getAtlassianStudioNLCreateUrl(cloudId, CONNIE_CREATE_SOURCE, csid);

	const handleClick = useCallback(() => {
		trackCreateSessionStart();
		onClick(CREATE_AGENT_ACTION_SUBJECT_ID);
	}, [onClick, trackCreateSessionStart]);

	return {
		ati: Ati.parse(CREATE_AGENT_ATI),
		children: <StudioRedirectCreateAgentMenuItem source={source} />,
		href: createAgentUrl,
		target: '_blank',
		onClick: handleClick,
		elemBefore: AiAgentIcon,
		testId: 'create-menu-link-item',
		isLoading,
	};
}
